import type { MatchRuleSet } from '@/core/schemas';

import { winsRequired } from '../matchRules';

import { resolveTurn } from './turn';
import type {
  CompletedLeg,
  Dart,
  EntryMode,
  GameState,
  LegState,
  Side,
  Turn,
  X01Config,
} from './types';

function other(side: Side): Side {
  return side === 'home' ? 'away' : 'home';
}

export interface CreateGameInput {
  readonly config: X01Config;
  readonly rules: MatchRuleSet;
  readonly entryMode: EntryMode;
  /** Which side throws first in the opening leg. */
  readonly startingSide: Side;
}

function freshLeg(config: X01Config, startedBy: Side): LegState {
  return {
    remaining: { home: config.startScore, away: config.startScore },
    turns: [],
    startedBy,
    toThrow: startedBy,
    winner: null,
  };
}

/**
 * Builds the initial scoring state for a match.
 *
 * Legs-only matches collapse to a single "set"; set-based matches take the
 * legs-per-set target from the rules. Both cases run through the same reducer.
 */
export function createGame(input: CreateGameInput): GameState {
  const { config, rules, entryMode, startingSide } = input;
  const usesSets = rules.scoring === 'sets';

  return {
    config,
    entryMode,
    legsToWin: usesSets ? (rules.legsPerSet ?? 1) : winsRequired(rules),
    setsToWin: usesSets ? winsRequired(rules) : 1,
    usesSets,
    setsWon: { home: 0, away: 0 },
    legsWon: { home: 0, away: 0 },
    completedLegs: [],
    currentLeg: freshLeg(config, startingSide),
    winner: null,
  };
}

export interface TurnInput {
  readonly points: number;
  readonly finishedOnDouble: boolean;
  readonly darts?: readonly Dart[];
}

/**
 * Folds one turn into the game state and returns the next state.
 *
 * Pure and total: a game that is already decided is returned unchanged, so a
 * double-submitted last throw cannot corrupt the result.
 */
export function applyTurn(state: GameState, input: TurnInput): GameState {
  if (state.winner !== null) {
    return state;
  }

  const leg = state.currentLeg;
  const side = leg.toThrow;
  const outcome = resolveTurn(
    leg.remaining[side],
    input.points,
    input.finishedOnDouble,
    state.config,
  );

  const turn: Turn = {
    side,
    points: outcome.kind === 'bust' ? 0 : input.points,
    finishedOnDouble: input.finishedOnDouble,
    bust: outcome.kind === 'bust',
    ...(input.darts !== undefined ? { darts: input.darts } : {}),
  };

  if (outcome.kind === 'score') {
    return {
      ...state,
      currentLeg: {
        ...leg,
        remaining: { ...leg.remaining, [side]: outcome.remaining },
        turns: [...leg.turns, turn],
        toThrow: other(side),
      },
    };
  }

  if (outcome.kind === 'bust') {
    return {
      ...state,
      currentLeg: {
        ...leg,
        turns: [...leg.turns, turn],
        toThrow: other(side),
      },
    };
  }

  // Leg won.
  return finishLeg(
    state,
    { ...leg, remaining: { ...leg.remaining, [side]: 0 }, turns: [...leg.turns, turn] },
    side,
  );
}

function finishLeg(state: GameState, leg: LegState, winnerSide: Side): GameState {
  const completed: CompletedLeg = {
    winner: winnerSide,
    turns: leg.turns,
    startedBy: leg.startedBy,
  };

  const legsWon = { ...state.legsWon, [winnerSide]: state.legsWon[winnerSide] + 1 };
  const nextStarter = other(leg.startedBy);

  // Set not yet decided: play another leg in the same set.
  if (legsWon[winnerSide] < state.legsToWin) {
    return {
      ...state,
      legsWon,
      completedLegs: [...state.completedLegs, completed],
      currentLeg: freshLeg(state.config, nextStarter),
    };
  }

  // Set won.
  const setsWon = { ...state.setsWon, [winnerSide]: state.setsWon[winnerSide] + 1 };

  // Match won.
  if (setsWon[winnerSide] >= state.setsToWin) {
    return {
      ...state,
      legsWon,
      setsWon,
      completedLegs: [...state.completedLegs, completed],
      currentLeg: { ...leg, winner: winnerSide },
      winner: winnerSide,
    };
  }

  // New set.
  return {
    ...state,
    setsWon,
    legsWon: { home: 0, away: 0 },
    completedLegs: [...state.completedLegs, completed],
    currentLeg: freshLeg(state.config, nextStarter),
  };
}

export interface GameScore {
  readonly winner: Side | null;
  readonly legs: Record<Side, number>;
  readonly sets: Record<Side, number> | null;
}

/**
 * The match result derived from the scoring state: total legs across the whole
 * match, sets when the match uses them, and the winner once decided.
 */
export function deriveScore(state: GameState): GameScore {
  const legs: Record<Side, number> = { home: 0, away: 0 };
  for (const leg of state.completedLegs) {
    legs[leg.winner] += 1;
  }

  return {
    winner: state.winner,
    legs,
    sets: state.usesSets ? state.setsWon : null,
  };
}
